import { createSlice } from '@reduxjs/toolkit';
import { getWs, clearWs } from '../connection';
import { addToAnimationQueue, setCurrentlyShuffling, clearAnimationQueue, markPlayerReceivingEffect, updateAnimationQueue } from './animationSlice';
import { showAlert, clearAlert, setOverlayBuyingCard, setYieldStatus } from './overlaySlice';

import { t } from "@lingui/macro";
import i18n from "../i18n";
import { startTutorial } from './tutorialSlice';
import { getExtraDiceCount } from '../playerUtils';

const NOT_IN_ROOM = 0;
const JOINING_ROOM = 1;
const LOADING_ROOM = 2;
const IN_ROOM = 3;

export const slice = createSlice({
  name: 'game',
  initialState: {
    players: [],
    myPlayerId: null,
    startTime: null,
    roomError: null,
    curTurnPlayerId: 1,
    rollsLeft: 3,
    diceRoll: [0, 0, 0, 0, 0, 0, 0, 0],
    rollDiceAtPos: [false, false, false, false, false, false, false, false],
    requestedDiceRoll: false,
    playerIdAtCityCenter: 0,
    playerIdAtLake: 0,
    cardsInTable: [],
    cardPileCount: 0,
    rollAccepted: false,
    status: NOT_IN_ROOM,
    playerDataAtRollTime: null,
    isTest: false
  },
  reducers: {
    setRoomId: (state, { payload }) => {
      state.roomId = payload;
    },
    join: (state, { payload }) => {
      state.roomId = payload.id;
      state.joined = payload.err === null;
      state.roomError = payload.err;
      state.isTest = payload.t === true;

      if (state.joined === false) {
        state.status = NOT_IN_ROOM;
      }
    },
    setGameStatus: (state, { payload }) => {
      state.status = payload;
    },
    leaveRoom: state => {
      if (state.joined || state.status > JOINING_ROOM) {
        // We'll need to make sure to close the websocket when we're leaving a room, too
        getWs(state.roomId).gsend({ action: "leave" });
      }

      state.status = NOT_IN_ROOM;
      state.roomId = null;
      state.joined = false;
      state.players = [];
      clearWs();
    },
    setMyPlayerId: (state, { payload }) => {
      state.myPlayerId = payload;
    },
    setPlayers: (state, { payload }) => {
      state.players = payload;
    },
    setPlayer: (state, { payload }) => {
      let existingPlayerIdx = state.players.findIndex((player) => player.id === payload.id);
      if (existingPlayerIdx !== -1) {
        state.players[existingPlayerIdx] = payload;
      } else {
        state.players.push(payload);
      }
    },
    setPlayerPropertyById: (state, { payload }) => {
      const player = state.players.find((player) => player.id === payload.id);
      if (player) {
        player[payload.property] = payload.value;
      } else {
        console.warn(`Tried to set property <${payload.property}> on player with id ${payload.id}, which doesn't exist`);
      }
    },
    removePlayer: (state, { payload }) => {
      state.players = state.players.filter((player) => player.id !== payload);
    },
    kickPlayer: (state, { payload }) => {
      getWs(state.roomId).gsend({
        action: 'kick-player',
        id: payload
      });
    },
    setStartTime: (state, { payload }) => {
      state.startTime = payload;
    },
    setRoomError: (state, { payload }) => {
      state.roomError = payload;
    },
    savePlayerName: (state, { payload }) => {
      getWs(state.roomId).gsend({ action: 'set-name', name: payload });
    },
    rollDice: state => {
      if (state.requestedDiceRoll) {
        return;
      }

      state.requestedDiceRoll = true;
      const isFirstRoll = state.diceRoll[0] === 0;
      if (isFirstRoll) {
        const curPlayer = state.players.find((player) => player.id === state.curTurnPlayerId);
        for (let i = 0; i < 6 + getExtraDiceCount(curPlayer); ++i) {
          state.rollDiceAtPos[i] = true;
        }
      }

      getWs(state.roomId).gsend({
        action: 'roll-dice',
        roll_set: state.rollDiceAtPos
      });
    },
    updateDice: (state, { payload }) => {
      state.diceRoll = payload;
    },
    clearDiceToRoll: state => {
      state.rollDiceAtPos = [false, false, false, false, false, false, false, false];
      state.requestedDiceRoll = false;
    },
    setRollsLeft: (state, { payload }) => {
      state.rollsLeft = payload;
    },
    markDieForRoll: (state, { payload }) => {
      state.rollDiceAtPos[payload.pos] = payload.mark === true;
    },
    acceptRoll: (state) => {
      getWs(state.roomId).gsend({
        action: 'accept-roll'
      });
    },
    setRollAccepted: state => {
      state.rollAccepted = true;
    },
    endTurn: (state) => {
      getWs(state.roomId).gsend({
        action: 'end-turn'
      });
    },
    changeTableCards: (state) => {
      getWs(state.roomId).gsend({
        action: 'change-table-cards'
      });
    },
    turnEnd: (state, { payload }) => {
      state.rollsLeft = payload.rolls_left;
      state.curTurnPlayerId = payload.next_turn_pid;
      state.diceRoll = [0, 0, 0, 0, 0, 0];
      state.rollAccepted = false;
    },
    setCurTurnPlayerId: (state, { payload }) => {
      state.curTurnPlayerId = payload;
    },
    updateBoardPlayers: (state, { payload }) => {
      state.playerIdAtCityCenter = payload.center;
      state.playerIdAtLake = payload.lake;
    },
    updateTableCards: (state, { payload }) => {
      state.cardsInTable = payload.cards;
      state.cardPileCount = payload.pile_count;
    },
    buyTableCard: (state, { payload }) => {
      getWs(state.roomId).gsend({
        action: 'buy-table-card',
        pos: payload
      });
    },
    playCard: (state, { payload }) => {
      getWs(state.roomId).gsend({
        action: 'play-card',
        pos: payload
      });
    },
    startGame: state => {
      getWs(state.roomId).gsend({ action: "start-game" });
    },
    answerYield: (state, { payload }) => {
      getWs(state.roomId).gsend({ action: "yield-answer", yield: payload });
    },
    setPlayerDataAtRollTime: (state, { payload }) => {
      state.playerDataAtRollTime = payload;
    },
    updatePlayerOrder: (state, { payload }) => {
      const playerOrderById = payload;
      state.players.sort((playerA, playerB) => {
        return playerOrderById.indexOf(playerA.id) >= playerOrderById.indexOf(playerB.id) ? 1 : -1;
      })
    },
    startGameAgain: state => {
      getWs(state.roomId).gsend({ action: 'start-again' });
    },
    pickMonster: (state, { payload }) => {
      getWs(state.roomId).gsend({
        action: 'pick-monster',
        monster_id: payload
      });
    },
  },
});

export const {
  setRoomId, setGameStatus, setPlayer, leaveRoom,
  setRoomError, removePlayer, kickPlayer, savePlayerName, rollDice,
  updateDice, setRollsLeft, setRollAccepted, markDieForRoll, acceptRoll,
  setCurTurnPlayerId, endTurn, turnEnd, clearDiceToRoll,
  updateBoardPlayers, updateTableCards, changeTableCards,
  buyTableCard, playCard, setPlayerPropertyById, startGame, answerYield,
  setPlayerDataAtRollTime, startGameAgain, pickMonster, setMyPlayerId,
  setPlayers
} = slice.actions;

export const requestMarkDie = data => (dispatch, getState) => {
  dispatch(markDieForRoll({
    pos: data.pos,
    mark: data.mark
  }));

  getWs(getState().game.roomId).gsend({
    action: 'mark-die',
    pos: data.pos,
    mark: data.mark
  });
};

const showYieldOverlay = yieldStatus => (dispatch, getState) => {
  const state = getState();
  dispatch(setYieldStatus(yieldStatus));

  if (state.animation.animationQueue.some((anim) => anim.type === 'yielding-decision') === false) {
    // Queue the yield animation
    dispatch(addToAnimationQueue({
      type: "yielding-decision",
      duration: yieldStatus.duration + 7500,
      onStart: [setYieldStatus(yieldStatus)], // Increase duration when this animation starts playing
      usesOverlay: false,
    }));
  } else {
  }
};

export const loadRoom = roomId => (dispatch, getState) => {
  if (getState().game.status === LOADING_ROOM) {
    return;
  }

  dispatch(setGameStatus(LOADING_ROOM));
  const w = getWs(roomId);
  w.gsend({ action: 'load' });

  w.setCallback('onMessage', (ws, data) => {
    if (data.action === 'load-err') {
      dispatch(setRoomError(data.message));
    }
    else if (data.action === 'game-info') {
      dispatch(clearAnimationQueue());
      dispatch(setMyPlayerId(data.my_pid));
      dispatch(slice.actions.setStartTime(data.started_at));
      dispatch(setCurTurnPlayerId(data.cur_turn_player_id));
      dispatch(setPlayers(data.players));
      dispatch(setRollsLeft(data.rolls_left));
      if (data.rolls_left === 0) {
        dispatch(setRollAccepted(true));
      }
      dispatch(clearDiceToRoll());
      dispatch(updateDice(data.dice_roll));
      dispatch(updateBoardPlayers({
        center: data.player_city_center,
        lake: data.player_lake
      }));
      dispatch(updateTableCards({
        cards: data.table_cards,
        pile_count: data.card_pile_count
      }));

      dispatch(setGameStatus(IN_ROOM));
      dispatch(setPlayerDataAtRollTime({
        health: data.player_health_at_roll_time,
        town_players: data.players_in_town_at_roll_time
      }));

      if (data.winner !== 0) {
        let gameResult = data.my_pid === data.winner ? 'victory' : 'game-over';
        dispatch(addToAnimationQueue({
          type: 'game-end',
          gameResult: gameResult,
          duration: 999999
        }));
      }

      if (data.yield_status.is_yielding) {
        dispatch(showYieldOverlay(data.yield_status));
      }

      if (data.started_at && !localStorage.getItem("tutorial-seen")) {
        dispatch(startTutorial());
      }
    }
    else if (data.action === "game-start") {
      dispatch(setCurTurnPlayerId(data.player_starting_id));
      dispatch(slice.actions.setStartTime(data.when));
      dispatch(slice.actions.updatePlayerOrder(data.new_player_order));

      if (!localStorage.getItem("tutorial-seen")) {
        dispatch(startTutorial());
      } else {
        const startingPlayer = getState().game.players.find((player) => player.id === data.player_starting_id);
        dispatch(addToAnimationQueue({
          type: "changing-turn",
          duration: 3000,
          next_player: startingPlayer,
          game_start: true
        }));
      }
    }
    else if (data.action === "player-update" || data.action === "player-join") {
      dispatch(setPlayer(data.player));
    }
    else if (data.action === "player-connectivity-change") {
      dispatch(setPlayerPropertyById({
        id: data.player_id,
        property: "is_connected",
        value: data.is_connected
      }));
    }
    else if (data.action === "player-leave") {
      if (data.forced && data.player_id === getState().game.myPlayerId) {
        dispatch(setRoomError("KICKED_FROM_ROOM"));
        dispatch(setGameStatus(LOADING_ROOM));
        clearWs();
      } else {
        dispatch(removePlayer(data.player_id));
      }
    }
    else if (data.action === "room-closed") {
      const roomError = data.closer === "user" ? "GAME_ROOM_CLOSED_BY_OWNER" : "GAME_ROOM_CLOSED_BY_SERVER";

      dispatch(setRoomError(roomError));
      dispatch(setGameStatus(LOADING_ROOM));
      clearWs();
    }
    else if (data.action === "die-marked") {
      dispatch(markDieForRoll({
        pos: data.pos,
        mark: data.mark
      }));
    }
    else if (data.action === "dice-roll") {
      const state = getState();
      function rand(min, max) {
        return Math.floor(Math.random() * (max + 1 - min) + min);
      }

      const randomDiceSet = (rollSet) => {
        let newDiceRoll = [];
        for (let i = 0; i < 8; ++i) {
          if (rollSet[i] !== true) {
            // Make sure we're only fake rolling the dice the user chose
            newDiceRoll[i] = state.game.diceRoll[i];
          } else {
            newDiceRoll[i] = rand(1, 6);
          }
        }

        return newDiceRoll;
      };

      const markDieActions = data.roll_set.map((mark, idx) => markDieForRoll({
        pos: idx,
        mark: mark
      }));

      // Roll the dice some times for fake, and then land on the real result
      const isFirstRoll = state.game.diceRoll[0] === 0;
      dispatch(addToAnimationQueue({
        type: "dice-roll",
        usesOverlay: false,
        onStart: [
          ...markDieActions,
          updateDice(randomDiceSet(data.roll_set)),
          setRollsLeft(data.rolls_left)
        ],
        duration: 375
      }));

      for (let i = 0; i < Math.round(Math.random()) + 2; ++i) {
        dispatch(addToAnimationQueue({
          type: "dice-roll",
          usesOverlay: false,
          onStart: updateDice(randomDiceSet(data.roll_set)),
          duration: 375
        }));
      }

      // Actually update the dice to the real roll now
      // This duration is a bit longer than it should, so the next animation triggers a bit later
      dispatch(addToAnimationQueue({
        type: "dice-roll",
        usesOverlay: false,
        onStart: updateDice(data.dice),
        onEnd: clearDiceToRoll(),
        duration: 375,
      }));
    }
    else if (data.action === "roll-accept") {
      dispatch(setPlayerDataAtRollTime({
        health: data.player_health_at_roll_time,
        town_players: data.players_in_town_at_roll_time
      }));

      let onStartActions = [clearDiceToRoll(), setRollsLeft(0)];
      if (data.events.length > 0) {
        let animationDelay = data.events.length === 1 && data.events[0][1] === "energy" ? 2500 : 2000;
        onStartActions.push({
          func: "startAnimatedPlayerDataChange",
          args: [data.events, animationDelay]
        });
      }

      // Add a fake animation to the queue to wait like 1 second before the dice effect triggers
      // in the event that the accept was due to running out of rolls
      if (data.forced_accept === true) {
        dispatch(addToAnimationQueue({
          type: "just-wait-fake-animation",
          usesOverlay: false,
          duration: 1000,
        }));
      }

      dispatch(addToAnimationQueue({
        type: "dice-roll-accepted",
        duration: data.events.length > 0 ? 3500 : 1000,
        events: data.events,
        onStart: onStartActions,
        onEnd: setRollAccepted(true)
      }));
    }
    else if (data.action === "turn-end") {
      const nextTurnPlayer = getState().game.players.find((player) => player.id === data.next_turn_pid);
      dispatch(addToAnimationQueue({
        type: "changing-turn",
        duration: 3000,
        next_player: nextTurnPlayer,
        onEnd: turnEnd({
          rolls_left: data.rolls_left,
          next_turn_pid: data.next_turn_pid
        })
      }));

      if (data.starting_at_city || data.starting_at_lake) {
        dispatch(addToAnimationQueue({
          type: "player-starting-turn",
          usesOverlay: false,
          duration: 2100,
          starting_at_city: data.starting_at_city,
          starting_at_lake: data.starting_at_lake,
          onStart: [{
            func: "startAnimatedPlayerDataChange",
            args: [[[nextTurnPlayer.id, "stars", 2]] /* events */, 1200 /* delay */]
          }]
        }));
      }
    }
    else if (data.action === "board-player-update") {
      const state = getState();
      let events = [];
      let enteringCity = false;
      let enteringLake = false;
      if (data.no_star_gain) {
        // Player will NOT be gaining a star. Just update the city
        // Abuse the animation system to make sure it's right
        dispatch(addToAnimationQueue({
          type: "fake-animation-enter-city",
          duration: 500,
          usesOverlay: false,
          onStart: updateBoardPlayers({
            lake: data.player_lake,
            center: data.player_city_center,
          })
        }));
      } else {
        if (data.player_city_center && state.game.playerIdAtCityCenter !== data.player_city_center) {
          enteringCity = true;
          events.push({
            func: "startAnimatedPlayerDataChange",
            args: [[[data.player_city_center, "stars", 1]] /* events */, 750 /* delay */]
          });
        }

        if (data.player_lake && state.game.playerIdAtLake !== data.player_lake) {
          enteringLake = true;
          events.push({
            func: "startAnimatedPlayerDataChange",
            args: [[[data.player_lake, "stars", 1]] /* events */, 750 /* delay */]
          });
        }

        // Abuse animation queue to queue this after whichever animation is ongoing
        dispatch(addToAnimationQueue({
          type: "player-entering-town",
          duration: 2000,
          entering_city: enteringCity,
          entering_lake: enteringLake,
          onStart: updateBoardPlayers({
            lake: data.player_lake,
            center: data.player_city_center,
          }),
          onEnd: events,
        }));
      }

    }
    else if (data.action === "table-card-update") {
      if (data.is_shuffle) {
        const cardsInTable = getState().game.cardsInTable;
        if (cardsInTable.length > 0) {
          const animationOldCardsAway = {
            type: "shuffle-new-cards",
            movementType: "shuffle-old-cards-away",
            cards: cardsInTable,
            duration: 2200,
            onStart: setCurrentlyShuffling(true)
          };
          dispatch(addToAnimationQueue(animationOldCardsAway));
        }

        if (data.cards.length > 0) {
          const animationNewCardsIn = {
            type: "shuffle-new-cards",
            movementType: "shuffle-new-cards-in",
            cards: data.cards,
            duration: 3000,
            onEnd: [
              updateTableCards({
                cards: data.cards,
                pile_count: data.card_pile_count
              }),
              setCurrentlyShuffling(false)
            ]
          };
          dispatch(addToAnimationQueue(animationNewCardsIn));
        }
      } else {
        dispatch(updateTableCards({
          cards: data.cards,
          pile_count: data.card_pile_count
        }));
      }
    }
    else if (data.action === "card-played") {
      const state = getState();
      const playingPlayer = selectCurrentTurnPlayer(state);

      const newPlayingPlayerCards = playingPlayer.cards.filter((_, pos) => pos !== data.card_pos);
      dispatch(setPlayerPropertyById({
        id: playingPlayer.id,
        property: "cards",
        value: newPlayingPlayerCards
      }));

      let delay = 6900;
      if (data.events.length === 1 && data.events[0][1] === "energy") {
        delay += 500;
      }

      dispatch(addToAnimationQueue({
        type: "playing-card-normal",
        cardType: data.card_type,
        events: data.events,
        duration: 8500,
        onStart: {
          func: "startAnimatedPlayerDataChange",
          args: [data.events, delay]
        }
      }));
    }
    else if (data.action === "table-card-buy") {
      const state = getState();
      const buyingPlayerIdx = state.game.players.findIndex((player) => player.id === data.buying_player_id);

      dispatch(setOverlayBuyingCard(null));
      dispatch(addToAnimationQueue({
        type: "buying-card",
        cardType: data.card,
        toPlayerIdx: buyingPlayerIdx,
        duration: 7500
      }));

      setTimeout(() => {
        const modifiedPlayer = getState().game.players[buyingPlayerIdx];
        // Copy the card set and update the player
        const newPlayerBuyingCards = JSON.parse(JSON.stringify(modifiedPlayer.cards));
        newPlayerBuyingCards.push(data.card);
        dispatch(setPlayerPropertyById({
          id: data.buying_player_id,
          property: "cards",
          value: newPlayerBuyingCards
        }));

        // Take the cost of the card in energy from the player
        dispatch(setPlayerPropertyById({
          id: data.buying_player_id,
          property: "energy",
          value: modifiedPlayer.energy - data.card_cost
        }));

        dispatch(markPlayerReceivingEffect({ id: data.buying_player_id, state: true }));
        setTimeout(() => {
          dispatch(markPlayerReceivingEffect({ id: data.buying_player_id, state: false }));
        }, 500);
      }, 4900);
    }
    else if (data.action === "cards-trigger") {
      if (data.card_events.length > 0 && data.card_list.length > 0) {
        let onStartActions = [];
        if (data.card_events.length > 0) {
          let animationDelay = data.card_events.length === 1 && data.card_events[0][1] === "energy" ? 5000 : 4500;
          onStartActions.push({
            func: "startAnimatedPlayerDataChange",
            args: [data.card_events, animationDelay]
          });
        }

        dispatch(addToAnimationQueue({
          type: "applying-keep-cards",
          duration: 6500,
          cards: data.card_list,
          events: data.card_events,
          playerName: data.player_name,
          onStart: onStartActions
        }));
      }
    }
    else if (data.action === "defeat") {
      setTimeout(() => {
        dispatch(addToAnimationQueue({
          type: 'game-end',
          gameResult: "game-over",
          duration: 999999
        }));
      }, 500);
    }
    else if (data.action === "victory") {
      setTimeout(() => {
        dispatch(addToAnimationQueue({
          type: 'game-end',
          gameResult: "victory",
          duration: 999999
        }));
      }, 500);
    }
    else if (data.action === "yield-status") {
      dispatch(showYieldOverlay(data));
    }
    else if (data.action === "yield-end") {
      let anim = getState().animation.animationQueue[0];
      if (anim && anim.type === "yielding-decision") {
        dispatch(updateAnimationQueue());
      }
    }
    else if (data.action === "death") {
      const player = getState().game.players.find((player) => player.id === data.player_id);
      if (player) {
        dispatch(addToAnimationQueue({
          type: "player-death",
          deathType: data.death_anim_type,
          monsterId: player.monster_id,
          duration: 3000
        }));
      }
    }
  });

  let disconnectedErrorShowing = false;
  let disconnectErrTimerHandle;
  w.setCallback('onClose', ws => {
    if (disconnectedErrorShowing || disconnectErrTimerHandle) {
      return;
    }

    disconnectErrTimerHandle = setTimeout(() => {
      disconnectedErrorShowing = true;
      dispatch(showAlert({
        canEsc: false,
        alertType: "error",
        alertTitle: i18n._(t`Houston, I can't connect to the server!`),
        alertText: i18n._(t`You've lost connection to the game server. This might just be a transient issue, but rest assured we will reconnect you as soon as possible so you can continue enjoying the game.`)
      }))
    }, 500);
  });

  w.setCallback('onCloseUntilReload', ws => {
    if (disconnectedErrorShowing || disconnectErrTimerHandle) {
      return;
    }

    dispatch(showAlert({
      canEsc: false,
      alertType: "error",
      alertTitle: i18n._(t`Disconnected from the server`),
      alertText: i18n._(t`You've been forcefully disconnected from the game server. This often happens because you have this same game room open in multiple tabs on your browser. Make sure that's not happening, and then reload this page to reconnect.`)
    }));
  });

  w.setCallback('onFirstConnect', (ws, data) => {
    clearTimeout(disconnectErrTimerHandle);
    disconnectErrTimerHandle = null;

    if (disconnectedErrorShowing) {
      disconnectedErrorShowing = false;
      dispatch(clearAlert());
    }
  });

  w.setCallback('onReconnect', (ws, data) => {
    w.gsend({ action: 'load' });

    clearTimeout(disconnectErrTimerHandle);
    disconnectErrTimerHandle = null;

    if (disconnectedErrorShowing) {
      disconnectedErrorShowing = false;
      dispatch(clearAlert());
    }
  });
};

export const joinRoom = id => (dispatch, getState) => {
  dispatch(setGameStatus(JOINING_ROOM));

  fetch(`/api/room/${id}/join`, {
    headers: {
      'User-Uuid': localStorage.getItem(`game-uuid-${id}`),
    },
    cache: 'no-store'
  }).then(function (response) {
    if (!response.ok) {
      throw Error(response.status);
    }
    return response;
  })
    .then((response) => {
      return response.json();
    })
    .then((data) => {
      if (data.ok) {
        localStorage.setItem(`game-uuid-${id}`, data.token);
        localStorage.setItem(`game-uuid-${id}-expire`, Date.now() + 86400 * 1000); // Max 1 day of token
        dispatch(slice.actions.join({
          id: id,
          err: null,
          t: data.t
        }));

        dispatch(loadRoom(id));
      } else {
        dispatch(slice.actions.join({
          id: id,
          err: data.error,
          t: data.t
        }));
      }
    }).catch(function (error) {
      if (Number(error.message) >= 400) {
        dispatch(setRoomError(i18n._(t`The game server failed while trying to join this game. Please, wait a few seconds and then try reloading this page in your browser`)));
      }
    });
};

export const createGame = history => (dispatch) => {
  fetch(`/api/room/create`, {
    cache: 'no-store'
  })
    .then((response) => {
      return response.json();
    })
    .then((data) => {
      if (data.ok) {
        dispatch(slice.actions.join({
          id: data.room_id,
          err: null
        }));
        dispatch(joinRoom(data.room_id));

        history.push(`/game/${data.room_id}`);
      }
    });
}

export const isJoiningRoom = state => state.game.status === JOINING_ROOM;
export const hasLoadedRoom = state => state.game.status === IN_ROOM;
export const isIngame = state => [LOADING_ROOM, IN_ROOM].includes(state.game.status);
export const hasGameStarted = state => state.game.startTime && state.game.startTime !== null;
export const selectMyPlayer = state => state.game.players ? state.game.players.find((player) => player.id === state.game.myPlayerId) : null;
export const selectGameStatus = state => state.game.status;
export const selectPlayers = state => state.game.players;
export const selectRoomId = state => state.game.roomId;
export const selectCurrentTurnPlayer = state => state.game.players.find((player) => player.id === state.game.curTurnPlayerId);
export const selectIsMyTurn = state => state.game.curTurnPlayerId === state.game.myPlayerId;
export const selectRollsLeft = state => state.game.rollsLeft;
export const selectHasRolledYet = state => state.game.diceRoll[0] !== 0;
export const selectDiceRoll = state => state.game.diceRoll;
export const selectPlayerAtCityCenter = state => state.game.playerIdAtCityCenter;
export const selectPlayerAtLake = state => selectIsLakeEnabled(state) ? state.game.playerIdAtLake : 0;
export const selectIsLakeEnabled = state => state.game.players.filter((player) => player.health > 0).length >= 5;
export const selectCardsInTable = state => state.game.cardsInTable;
export const selectCardPileCount = state => state.game.cardPileCount;
export const selectHasAcceptedRoll = state => state.game.rollAccepted;
export const selectPlayerDataAtRollTime = state => state.game.playerDataAtRollTime;
export const selectIsTestEnv = state => state.game.isTest;

export default slice.reducer;
